import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
  Download,
  Search,
  Sparkles,
  TrendingUp,
  MousePointerClick,
  Users,
  ShoppingBag,
  DollarSign,
  Flame,
  Target,
  Filter,
  X,
} from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";

import { getAnalyticsOverview } from "@/lib/analytics.functions";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PeriodFilter, usePeriodFilter, rangeForPreset } from "@/components/admin/PeriodFilter";
import { AdminGate } from "@/components/admin/AdminGate";
import { neutralizeCsvValue } from "@/lib/csv-safe";

export const Route = createFileRoute("/admin/dashboard-2")({
  head: () => ({
    meta: [
      { title: "Admin — Dashboard 2.0" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: () => (
    <AdminGate route="/admin/dashboard-2">
      <Dashboard2Page />
    </AdminGate>
  ),
});

// ============================================================================
// Types
// ============================================================================

type ViewRow = {
  path: string;
  session_id: string;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  device: string | null;
  created_at: string;
};
type ClickRow = {
  path: string;
  button_id: string | null;
  button_label: string | null;
  destination: string | null;
  device: string | null;
  created_at: string;
};
type LeadRow = {
  id: string;
  created_at: string;
  landing_path: string | null;
  origem: string | null;
  classificacao: string | null;
  score_total: number | null;
};
type SaleRow = {
  id: string;
  paid_at: string | null;
  created_at: string;
  customer_email: string | null;
  product_id: string | null;
  product_name: string | null;
  gross_value: number | null;
  revenue_value?: number | null;
  utm_content: string | null;
  utm_campaign: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  attributed_lead_id: string | null;
};
type Overview = {
  views: ViewRow[];
  clicks: ClickRow[];
  leads: LeadRow[];
  sales: SaleRow[];
};

// ============================================================================
// Helpers
// ============================================================================

const fmtBRL = (v: number) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 }).format(v);
const fmtNum = (v: number) => v.toLocaleString("pt-BR");
const pct = (num: number, den: number) => (den > 0 ? ((num / den) * 100).toFixed(1) + "%" : "—");
const delta = (curr: number, prev: number) => {
  if (prev === 0) return curr > 0 ? 100 : 0;
  return ((curr - prev) / prev) * 100;
};

// Mapear origem/landing_path do lead → path da LP
function pathFromLead(l: { landing_path: string | null; origem: string | null }): string | null {
  if (l.landing_path && l.landing_path.startsWith("/")) return l.landing_path;
  const o = l.origem ?? "";
  const aula = o.match(/^aulas?-exp(?:erimentais)?-([a-z0-9-]+?)(?:-parcial)?$/i);
  if (aula) return `/aulas-experimentais-${aula[1]}`;
  if (o.startsWith("revista")) return "/revista";
  if (o.startsWith("ementa-site-")) return `/ementa/${o.replace(/^ementa-site-/, "").replace(/-parcial$/, "")}`;
  if (o.startsWith("aprovafeo-")) return `/aprovafeo-${o.replace(/^aprovafeo-/, "").replace(/-parcial$/, "")}`;
  if (o.startsWith("imersao-pbe")) return "/imersao-pbe";
  if (o.startsWith("quizz-dor")) return "/quizz-dor-pos-todas";
  if (o.startsWith("quizz-pos")) return "/quizz-pos-todas";
  return null;
}

// Origem canônica (pra filtro): agrupa por família de captura
function sourceFamily(l: LeadRow): string {
  const o = (l.origem ?? "").toLowerCase();
  if (o.startsWith("aulas-exp") || o.startsWith("aulas-experimentais")) return "aulas-exp";
  if (o.startsWith("ementa")) return "ementa";
  if (o.startsWith("pre-checkout")) return "pre-checkout";
  if (o.startsWith("revista")) return "revista";
  if (o.startsWith("aprovafeo")) return "aprovafeo";
  if (o.startsWith("imersao")) return "imersao-pbe";
  if (o.startsWith("quizz")) return "quizz";
  return o || "outros";
}

// Classifica produto Eduzz → grupo curto
function productGroup(name: string | null | undefined): string {
  const n = (name ?? "").toLowerCase();
  if (!n) return "outros";
  if (n.includes("star")) return "star-cursos";
  if (n.includes("ortopedia") && !n.includes("traumatologia")) return "pos-orto";
  if (n.includes("dor")) return "pos-dor";
  if (n.includes("esporte")) return "pos-esporte";
  if (n.includes("gerontologia")) return "pos-geronto";
  if (n.includes("traumatologia")) return "pos-trauma";
  if (n.includes("prescri")) return "pos-prescricao";
  if (n.includes("passaporte")) return "passaporte";
  if (n.includes("cbfeo") || n.includes("congresso")) return "cbfeo";
  if (n.includes("certifica")) return "certificacoes";
  if (n.includes("imersão") || n.includes("imersao")) return "imersoes";
  return "outros";
}

const PRODUCT_LABEL: Record<string, string> = {
  "star-cursos": "Star Cursos",
  "pos-orto": "Pós Orto",
  "pos-dor": "Pós Dor",
  "pos-esporte": "Pós Esporte",
  "pos-geronto": "Pós Geronto",
  "pos-trauma": "Pós Trauma",
  "pos-prescricao": "Pós Prescrição",
  passaporte: "Passaporte FEO",
  cbfeo: "II CBFEO",
  certificacoes: "Certificações",
  imersoes: "Imersões",
  outros: "Outros",
};

const PRODUCT_COLOR: Record<string, string> = {
  "star-cursos": "#a855f7",
  "pos-orto": "#3b82f6",
  "pos-dor": "#ef4444",
  "pos-esporte": "#10b981",
  "pos-geronto": "#f59e0b",
  "pos-trauma": "#8b5cf6",
  "pos-prescricao": "#06b6d4",
  passaporte: "#f5b022",
  cbfeo: "#e11d48",
  certificacoes: "#14b8a6",
  imersoes: "#f97316",
  outros: "#64748b",
};

// Deduplicar sale (email, produto) — 1ª paga
function dedupSales(sales: SaleRow[]): SaleRow[] {
  const seen = new Set<string>();
  const out: SaleRow[] = [];
  const sorted = [...sales].sort((a, b) =>
    (a.paid_at ?? a.created_at).localeCompare(b.paid_at ?? b.created_at),
  );
  for (const s of sorted) {
    const k = `${(s.customer_email ?? "").toLowerCase()}|${s.product_id ?? ""}`;
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(s);
  }
  return out;
}

// CSV
function downloadCsv(name: string, rows: Record<string, string | number>[]) {
  if (rows.length === 0) {
    toast.error("Nada pra exportar");
    return;
  }
  const headers = Object.keys(rows[0]);
  const esc = (v: string | number) => {
    const s = neutralizeCsvValue(String(v ?? ""));
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [headers.join(","), ...rows.map((r) => headers.map((h) => esc(r[h])).join(","))].join("\n");
  const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${name}-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ============================================================================
// Page
// ============================================================================

function Dashboard2Page() {
  const period = usePeriodFilter("month");

  const { fromIso, toIso } = useMemo(() => {
    const to = period.range.to ?? new Date().toISOString();
    const from = period.range.from ?? new Date(2020, 0, 1).toISOString();
    return { fromIso: from, toIso: to };
  }, [period.range]);

  // Janela do período anterior (mesmo tamanho, imediatamente antes)
  const { prevFromIso, prevToIso } = useMemo(() => {
    if (period.preset === "yesterday") {
      const r = rangeForPreset("today");
      return { prevFromIso: r.from, prevToIso: r.to };
    }
    const from = new Date(fromIso).getTime();
    const to = new Date(toIso).getTime();
    const span = Math.max(to - from, 1);
    return {
      prevFromIso: new Date(from - span).toISOString(),
      prevToIso: new Date(from).toISOString(),
    };
  }, [fromIso, toIso, period.preset]);

  const fetchOverview = useServerFn(getAnalyticsOverview);
  const curr = useQuery({
    queryKey: ["dash2", "curr", fromIso, toIso],
    queryFn: () => fetchOverview({ data: { fromIso, toIso } }) as Promise<Overview>,
  });
  const prev = useQuery({
    queryKey: ["dash2", "prev", prevFromIso, prevToIso],
    queryFn: () => fetchOverview({ data: { fromIso: prevFromIso!, toIso: prevToIso! } }) as Promise<Overview>,
    enabled: !!prevFromIso && !!prevToIso && period.preset !== "all",
  });

  // Filtros globais
  const [pathSearch, setPathSearch] = useState("");
  const [sourceFilter, setSourceFilter] = useState<string>("");
  const [mediumFilter, setMediumFilter] = useState<string>("");
  const [campaignFilter, setCampaignFilter] = useState<string>("");
  const [deviceFilter, setDeviceFilter] = useState<string>("");
  const [classFilter, setClassFilter] = useState<string>("");

  const clearFilters = () => {
    setPathSearch("");
    setSourceFilter("");
    setMediumFilter("");
    setCampaignFilter("");
    setDeviceFilter("");
    setClassFilter("");
  };
  const activeFiltersCount =
    [pathSearch, sourceFilter, mediumFilter, campaignFilter, deviceFilter, classFilter].filter(Boolean).length;

  const overview = curr.data;
  const prevOverview = prev.data;

  // Aplica filtros
  const filtered = useMemo(() => {
    if (!overview) return null;
    const ps = pathSearch.trim().toLowerCase();
    const matchView = (v: ViewRow) =>
      (!ps || v.path.toLowerCase().includes(ps)) &&
      (!sourceFilter || (v.utm_source ?? "") === sourceFilter) &&
      (!mediumFilter || (v.utm_medium ?? "") === mediumFilter) &&
      (!campaignFilter || (v.utm_campaign ?? "") === campaignFilter) &&
      (!deviceFilter || (v.device ?? "desconhecido") === deviceFilter);
    const matchClick = (c: ClickRow) =>
      (!ps || c.path.toLowerCase().includes(ps)) &&
      (!deviceFilter || (c.device ?? "desconhecido") === deviceFilter);
    const matchLead = (l: LeadRow) => {
      const p = pathFromLead(l);
      return (
        (!ps || (p ?? "").toLowerCase().includes(ps)) &&
        (!classFilter || (l.classificacao ?? "") === classFilter)
      );
    };
    const matchSale = (s: SaleRow) =>
      (!sourceFilter || (s.utm_source ?? "") === sourceFilter) &&
      (!mediumFilter || (s.utm_medium ?? "") === mediumFilter) &&
      (!campaignFilter || (s.utm_campaign ?? "") === campaignFilter);
    return {
      views: overview.views.filter(matchView),
      clicks: overview.clicks.filter(matchClick),
      leads: overview.leads.filter(matchLead),
      sales: dedupSales(overview.sales.filter(matchSale)),
    };
  }, [overview, pathSearch, sourceFilter, mediumFilter, campaignFilter, deviceFilter, classFilter]);

  const prevFiltered = useMemo(() => {
    if (!prevOverview) return null;
    // Aplica os mesmos filtros no período anterior pra delta consistente
    return {
      views: prevOverview.views.filter(
        (v) =>
          (!pathSearch || v.path.toLowerCase().includes(pathSearch.toLowerCase())) &&
          (!sourceFilter || (v.utm_source ?? "") === sourceFilter) &&
          (!mediumFilter || (v.utm_medium ?? "") === mediumFilter) &&
          (!campaignFilter || (v.utm_campaign ?? "") === campaignFilter) &&
          (!deviceFilter || (v.device ?? "desconhecido") === deviceFilter),
      ),
      clicks: prevOverview.clicks.filter(
        (c) =>
          (!pathSearch || c.path.toLowerCase().includes(pathSearch.toLowerCase())) &&
          (!deviceFilter || (c.device ?? "desconhecido") === deviceFilter),
      ),
      leads: prevOverview.leads.filter((l) => {
        const p = pathFromLead(l);
        return (
          (!pathSearch || (p ?? "").toLowerCase().includes(pathSearch.toLowerCase())) &&
          (!classFilter || (l.classificacao ?? "") === classFilter)
        );
      }),
      sales: dedupSales(
        prevOverview.sales.filter(
          (s) =>
            (!sourceFilter || (s.utm_source ?? "") === sourceFilter) &&
            (!mediumFilter || (s.utm_medium ?? "") === mediumFilter) &&
            (!campaignFilter || (s.utm_campaign ?? "") === campaignFilter),
        ),
      ),
    };
  }, [prevOverview, pathSearch, sourceFilter, mediumFilter, campaignFilter, deviceFilter, classFilter]);

  const isLoading = curr.isLoading;

  return (
    <div className="min-h-screen text-white">
      <div className="mx-auto max-w-[1400px] px-4 py-6 sm:py-8">
        <Header
          isFetching={curr.isFetching}
          onRefresh={() => {
            curr.refetch();
            prev.refetch();
            toast.success("Atualizando…");
          }}
        />

        {/* Controles do período + filtros */}
        <Card className="p-4 mb-6 bg-slate-900/60 border-slate-800">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            <div className="flex items-center gap-2 min-w-0">
              <PeriodFilter
                preset={period.preset}
                setPreset={period.setPreset}
                customFrom={period.customFrom}
                setCustomFrom={period.setCustomFrom}
                customTo={period.customTo}
                setCustomTo={period.setCustomTo}
              />
            </div>
            <div className="flex-1 flex flex-wrap items-center gap-2">
              <div className="relative flex-1 min-w-[180px] max-w-xs">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-white/45" />
                <Input
                  placeholder="Buscar página (ex: /pos)"
                  value={pathSearch}
                  onChange={(e) => setPathSearch(e.target.value)}
                  className="pl-7 h-8 text-xs bg-slate-950/50 border-slate-800"
                />
              </div>
              <FilterSelect
                value={sourceFilter}
                setValue={setSourceFilter}
                placeholder="Source"
                options={uniqueVals(overview?.views ?? [], (v) => v.utm_source)}
              />
              <FilterSelect
                value={mediumFilter}
                setValue={setMediumFilter}
                placeholder="Medium"
                options={uniqueVals(overview?.views ?? [], (v) => v.utm_medium)}
              />
              <FilterSelect
                value={campaignFilter}
                setValue={setCampaignFilter}
                placeholder="Campanha"
                options={uniqueVals(overview?.views ?? [], (v) => v.utm_campaign)}
              />
              <FilterSelect
                value={deviceFilter}
                setValue={setDeviceFilter}
                placeholder="Device"
                options={["mobile", "desktop", "tablet", "desconhecido"]}
              />
              <FilterSelect
                value={classFilter}
                setValue={setClassFilter}
                placeholder="Classif."
                options={["quente", "morno", "frio"]}
              />
              {activeFiltersCount > 0 && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="h-8 text-xs text-white/55 hover:text-white">
                  <X className="h-3 w-3 mr-1" /> Limpar ({activeFiltersCount})
                </Button>
              )}
            </div>
          </div>
        </Card>

        {isLoading || !filtered ? (
          <LoadingSkeleton />
        ) : (
          <DashboardContent
            data={filtered}
            prev={prevFiltered}
            hasComparison={period.preset !== "all"}
            fromIso={fromIso}
            toIso={toIso}
          />
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Header
// ============================================================================

function Header({ isFetching, onRefresh }: { isFetching: boolean; onRefresh: () => void }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
      <div className="min-w-0">
        <Button asChild variant="outline" size="sm" className="mb-3 bg-slate-900/50 border-slate-800 hover:bg-slate-800 text-white/70">
          <Link to="/admin"><ArrowLeft className="h-4 w-4 mr-1" /> Voltar ao menu</Link>
        </Button>
        <div className="flex items-center gap-2">
          <Sparkles className="h-6 w-6 text-admin-accent" />
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Dashboard 2.0</h1>
          <Badge className="bg-[#f5b022]/20 text-admin-accent border-[#f5b022]/30 hover:bg-[#f5b022]/25">Beta</Badge>
        </div>
        <p className="text-xs sm:text-sm text-white/55 mt-1">
          Central de marketing — tráfego, conversão, produtos e atribuição em um só lugar.
        </p>
      </div>
      <Button variant="outline" size="sm" onClick={onRefresh} disabled={isFetching} className="bg-slate-900/50 border-slate-800 hover:bg-slate-800 text-white/70">
        <RefreshCw className={`h-4 w-4 mr-2 ${isFetching ? "animate-spin" : ""}`} /> Atualizar
      </Button>
    </div>
  );
}

// ============================================================================
// Content (com dados filtrados)
// ============================================================================

function DashboardContent({
  data,
  prev,
  hasComparison,
  fromIso,
  toIso,
}: {
  data: Overview;
  prev: Overview | null;
  hasComparison: boolean;
  fromIso: string;
  toIso: string;
}) {
  const { views, clicks, leads, sales } = data;

  const kpis = useMemo(() => computeKpis(data), [data]);
  const prevKpis = useMemo(() => (prev ? computeKpis(prev) : null), [prev]);

  return (
    <>
      {/* KPI Grid */}
      <div className="grid gap-3 grid-cols-2 md:grid-cols-4 mb-6">
        <KpiCard icon={<Users className="h-4 w-4" />} label="Sessões únicas" value={fmtNum(kpis.sessions)} delta={hasComparison && prevKpis ? delta(kpis.sessions, prevKpis.sessions) : null} color="blue" />
        <KpiCard icon={<TrendingUp className="h-4 w-4" />} label="Visualizações" value={fmtNum(kpis.views)} delta={hasComparison && prevKpis ? delta(kpis.views, prevKpis.views) : null} color="cyan" />
        <KpiCard icon={<MousePointerClick className="h-4 w-4" />} label="Cliques CTA" value={fmtNum(kpis.clicks)} hint={`CTR ${pct(kpis.clicks, kpis.sessions)}`} delta={hasComparison && prevKpis ? delta(kpis.clicks, prevKpis.clicks) : null} color="emerald" />
        <KpiCard icon={<Target className="h-4 w-4" />} label="Leads" value={fmtNum(kpis.leads)} hint={`Conv. ${pct(kpis.leads, kpis.sessions)}`} delta={hasComparison && prevKpis ? delta(kpis.leads, prevKpis.leads) : null} color="amber" />
        <KpiCard icon={<Flame className="h-4 w-4" />} label="Leads quentes" value={fmtNum(kpis.hot)} hint={`${pct(kpis.hot, kpis.leads)} do total`} delta={hasComparison && prevKpis ? delta(kpis.hot, prevKpis.hot) : null} color="rose" />
        <KpiCard icon={<ShoppingBag className="h-4 w-4" />} label="Vendas reais" value={fmtNum(kpis.salesCount)} hint={`${pct(kpis.salesCount, kpis.leads)} dos leads`} delta={hasComparison && prevKpis ? delta(kpis.salesCount, prevKpis.salesCount) : null} color="violet" />
        <KpiCard icon={<DollarSign className="h-4 w-4" />} label="Faturamento" value={fmtBRL(kpis.revenue)} delta={hasComparison && prevKpis ? delta(kpis.revenue, prevKpis.revenue) : null} color="emerald" />
        <KpiCard icon={<DollarSign className="h-4 w-4" />} label="Ticket médio" value={fmtBRL(kpis.aov)} hint={`R$/sessão ${(kpis.sessions > 0 ? kpis.revenue / kpis.sessions : 0).toFixed(2)}`} color="teal" />
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="bg-slate-900/60 border border-slate-800 mb-4 h-auto flex flex-wrap">
          <TabsTrigger value="overview">Visão geral</TabsTrigger>
          <TabsTrigger value="traffic">Tráfego</TabsTrigger>
          <TabsTrigger value="conversion">Conversão</TabsTrigger>
          <TabsTrigger value="products">Produtos</TabsTrigger>
          <TabsTrigger value="attribution">Atribuição</TabsTrigger>
          <TabsTrigger value="insights">Insights</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6 mt-0">
          <OverviewTab views={views} clicks={clicks} leads={leads} sales={sales} fromIso={fromIso} toIso={toIso} />
        </TabsContent>
        <TabsContent value="traffic" className="space-y-6 mt-0">
          <TrafficTab views={views} clicks={clicks} />
        </TabsContent>
        <TabsContent value="conversion" className="space-y-6 mt-0">
          <ConversionTab views={views} clicks={clicks} leads={leads} sales={sales} />
        </TabsContent>
        <TabsContent value="products" className="space-y-6 mt-0">
          <ProductsTab sales={sales} leads={leads} />
        </TabsContent>
        <TabsContent value="attribution" className="space-y-6 mt-0">
          <AttributionTab views={views} leads={leads} sales={sales} />
        </TabsContent>
        <TabsContent value="insights" className="space-y-6 mt-0">
          <InsightsTab data={data} prev={prev} hasComparison={hasComparison} />
        </TabsContent>
      </Tabs>
    </>
  );
}

// ============================================================================
// KPI helpers
// ============================================================================

function computeKpis(d: Overview) {
  const sessions = new Set(d.views.map((v) => v.session_id)).size;
  const views = d.views.length;
  const clicks = d.clicks.length;
  const leadsCount = d.leads.length;
  const hot = d.leads.filter((l) => l.classificacao === "quente").length;
  const salesCount = d.sales.length;
  const revenue = d.sales.reduce((s, r) => s + Number(r.revenue_value ?? r.gross_value ?? 0), 0);
  const aov = salesCount > 0 ? revenue / salesCount : 0;
  return { sessions, views, clicks, leads: leadsCount, hot, salesCount, revenue, aov };
}

function KpiCard({
  icon,
  label,
  value,
  hint,
  delta,
  color,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  hint?: string;
  delta?: number | null;
  color: string;
}) {
  const colorMap: Record<string, string> = {
    blue: "text-blue-400 bg-blue-500/10",
    cyan: "text-cyan-400 bg-cyan-500/10",
    emerald: "text-emerald-400 bg-emerald-500/10",
    amber: "text-admin-accent bg-amber-500/10",
    rose: "text-rose-400 bg-rose-500/10",
    violet: "text-violet-400 bg-violet-500/10",
    teal: "text-teal-400 bg-teal-500/10",
  };
  return (
    <Card className="p-4 bg-slate-900/60 border-slate-800 hover:border-slate-700 transition-colors">
      <div className="flex items-start justify-between gap-2">
        <div className={`inline-flex h-8 w-8 items-center justify-center rounded-lg ${colorMap[color] ?? colorMap.blue}`}>
          {icon}
        </div>
        {delta !== null && delta !== undefined && (
          <span className={`inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded ${delta >= 0 ? "text-emerald-400 bg-emerald-500/10" : "text-rose-400 bg-rose-500/10"}`}>
            {delta >= 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
            {Math.abs(delta).toFixed(0)}%
          </span>
        )}
      </div>
      <p className="text-[10px] uppercase tracking-wider text-white/55 mt-3">{label}</p>
      <p className="text-2xl font-bold text-white mt-0.5">{value}</p>
      {hint && <p className="text-[10px] text-white/45 mt-0.5">{hint}</p>}
    </Card>
  );
}

// ============================================================================
// Filter select (light)
// ============================================================================

function FilterSelect({
  value,
  setValue,
  placeholder,
  options,
}: {
  value: string;
  setValue: (v: string) => void;
  placeholder: string;
  options: string[];
}) {
  return (
    <div className="relative">
      <Filter className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-white/45 pointer-events-none" />
      <select
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className="h-8 pl-6 pr-2 text-xs bg-slate-950/50 border border-slate-800 rounded-md text-white/70 hover:border-slate-700 focus:outline-none focus:ring-1 focus:ring-[#f5b022]"
      >
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}

function uniqueVals<T>(arr: T[], get: (x: T) => string | null): string[] {
  const s = new Set<string>();
  for (const x of arr) {
    const v = get(x);
    if (v) s.add(v);
  }
  return Array.from(s).sort();
}

function LoadingSkeleton() {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {Array.from({ length: 8 }).map((_, i) => (
          <Card key={i} className="p-4 bg-slate-900/60 border-slate-800 h-28 animate-pulse" />
        ))}
      </div>
      <Card className="p-4 bg-slate-900/60 border-slate-800 h-64 animate-pulse" />
    </div>
  );
}

// ============================================================================
// TAB: OVERVIEW
// ============================================================================

function OverviewTab({
  views,
  clicks,
  leads,
  sales,
  fromIso,
  toIso,
}: {
  views: ViewRow[];
  clicks: ClickRow[];
  leads: LeadRow[];
  sales: SaleRow[];
  fromIso: string;
  toIso: string;
}) {
  // Série diária
  const daily = useMemo(() => {
    const map = new Map<string, { day: string; Sessões: Set<string>; Cliques: number; Leads: number; Vendas: number; Faturamento: number }>();
    const spanDays = Math.max(1, Math.ceil((new Date(toIso).getTime() - new Date(fromIso).getTime()) / 86400000));
    // Semear todos os dias (mesmo zerados) até 90 dias
    if (spanDays <= 90) {
      for (let i = 0; i < spanDays; i++) {
        const d = new Date(new Date(fromIso).getTime() + i * 86400000).toISOString().slice(0, 10);
        map.set(d, { day: d, Sessões: new Set(), Cliques: 0, Leads: 0, Vendas: 0, Faturamento: 0 });
      }
    }
    const ensure = (d: string) => {
      const r = map.get(d) ?? { day: d, Sessões: new Set<string>(), Cliques: 0, Leads: 0, Vendas: 0, Faturamento: 0 };
      map.set(d, r);
      return r;
    };
    for (const v of views) {
      const r = ensure(v.created_at.slice(0, 10));
      r.Sessões.add(v.session_id);
    }
    for (const c of clicks) ensure(c.created_at.slice(0, 10)).Cliques += 1;
    for (const l of leads) ensure(l.created_at.slice(0, 10)).Leads += 1;
    for (const s of sales) {
      const d = (s.paid_at ?? s.created_at).slice(0, 10);
      const r = ensure(d);
      r.Vendas += 1;
      r.Faturamento += Number(s.revenue_value ?? s.gross_value ?? 0);
    }
    return Array.from(map.values())
      .sort((a, b) => a.day.localeCompare(b.day))
      .map((r) => ({ day: r.day.slice(5), Sessões: r.Sessões.size, Cliques: r.Cliques, Leads: r.Leads, Vendas: r.Vendas, Faturamento: Math.round(r.Faturamento) }));
  }, [views, clicks, leads, sales, fromIso, toIso]);

  // Funnel
  const sessions = new Set(views.map((v) => v.session_id)).size;
  const clicksCount = clicks.length;
  const leadsCount = leads.length;
  const hot = leads.filter((l) => l.classificacao === "quente").length;
  const salesCount = sales.length;

  return (
    <>
      <Card className="p-5 bg-slate-900/60 border-slate-800">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-semibold text-white">Evolução diária</h2>
            <p className="text-xs text-white/55">Sessões, cliques, leads, vendas e faturamento por dia.</p>
          </div>
        </div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={daily} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
              <defs>
                <linearGradient id="gSess" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.6} />
                  <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gLead" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.6} />
                  <stop offset="100%" stopColor="#f59e0b" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gSale" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10b981" stopOpacity={0.7} />
                  <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="day" fontSize={10} stroke="#64748b" />
              <YAxis fontSize={10} stroke="#64748b" allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 8, fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Area type="monotone" dataKey="Sessões" stroke="#3b82f6" fill="url(#gSess)" strokeWidth={2} />
              <Area type="monotone" dataKey="Leads" stroke="#f59e0b" fill="url(#gLead)" strokeWidth={2} />
              <Area type="monotone" dataKey="Vendas" stroke="#10b981" fill="url(#gSale)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="p-5 bg-slate-900/60 border-slate-800">
          <h2 className="font-semibold text-white mb-1">Funil de conversão</h2>
          <p className="text-xs text-white/55 mb-5">Do 1º acesso até a venda paga.</p>
          <FunnelBars
            steps={[
              { label: "Sessões únicas", value: sessions, color: "#3b82f6" },
              { label: "Cliques em CTA", value: clicksCount, color: "#06b6d4" },
              { label: "Leads capturados", value: leadsCount, color: "#f59e0b" },
              { label: "Leads quentes", value: hot, color: "#ef4444" },
              { label: "Vendas pagas", value: salesCount, color: "#10b981" },
            ]}
          />
        </Card>

        <Card className="p-5 bg-slate-900/60 border-slate-800">
          <h2 className="font-semibold text-white mb-1">Faturamento acumulado</h2>
          <p className="text-xs text-white/55 mb-4">Curva de receita paga no período.</p>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={daily.reduce<{ day: string; Acumulado: number }[]>((acc, cur, i) => {
                  const prev = i > 0 ? acc[i - 1].Acumulado : 0;
                  acc.push({ day: cur.day, Acumulado: prev + cur.Faturamento });
                  return acc;
                }, [])}
                margin={{ top: 10, right: 10, left: 10, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis dataKey="day" fontSize={10} stroke="#64748b" />
                <YAxis fontSize={10} stroke="#64748b" tickFormatter={(v) => `R$${Math.round(v / 1000)}k`} />
                <Tooltip
                  contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 8, fontSize: 12 }}
                  formatter={(v: number) => fmtBRL(v)}
                />
                <Line type="monotone" dataKey="Acumulado" stroke="#f5b022" strokeWidth={2.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>
    </>
  );
}

function FunnelBars({ steps }: { steps: { label: string; value: number; color: string }[] }) {
  const max = Math.max(1, ...steps.map((s) => s.value));
  return (
    <div className="space-y-3">
      {steps.map((s, i) => {
        const pctBar = (s.value / max) * 100;
        const prev = i > 0 ? steps[i - 1].value : s.value;
        const conv = i > 0 && prev > 0 ? ((s.value / prev) * 100).toFixed(1) + "%" : null;
        return (
          <div key={s.label}>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-white/70 font-medium">{s.label}</span>
              <span className="flex items-center gap-2">
                <span className="text-white font-semibold">{fmtNum(s.value)}</span>
                {conv && <span className="text-white/45 text-[10px]">↳ {conv}</span>}
              </span>
            </div>
            <div className="h-6 bg-slate-950/50 rounded-md overflow-hidden">
              <div
                className="h-full rounded-md transition-all duration-500"
                style={{ width: `${pctBar}%`, background: `linear-gradient(90deg, ${s.color}, ${s.color}dd)` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ============================================================================
// TAB: TRAFFIC
// ============================================================================

function TrafficTab({ views, clicks }: { views: ViewRow[]; clicks: ClickRow[] }) {
  // Fontes (source × medium)
  const sources = useMemo(() => {
    const map = new Map<string, { key: string; source: string; medium: string; sessions: Set<string>; views: number }>();
    for (const v of views) {
      const source = v.utm_source ?? "(direto)";
      const medium = v.utm_medium ?? "(nenhum)";
      const key = `${source}|${medium}`;
      const r = map.get(key) ?? { key, source, medium, sessions: new Set<string>(), views: 0 };
      r.sessions.add(v.session_id);
      r.views += 1;
      map.set(key, r);
    }
    return Array.from(map.values())
      .map((r) => ({ ...r, sessionsCount: r.sessions.size }))
      .sort((a, b) => b.sessionsCount - a.sessionsCount);
  }, [views]);

  // Campanhas
  const campaigns = useMemo(() => {
    const map = new Map<string, { campaign: string; sessions: Set<string>; source: string; medium: string }>();
    for (const v of views) {
      if (!v.utm_campaign) continue;
      const r = map.get(v.utm_campaign) ?? {
        campaign: v.utm_campaign,
        sessions: new Set<string>(),
        source: v.utm_source ?? "—",
        medium: v.utm_medium ?? "—",
      };
      r.sessions.add(v.session_id);
      map.set(v.utm_campaign, r);
    }
    return Array.from(map.values())
      .map((r) => ({ ...r, sessionsCount: r.sessions.size }))
      .sort((a, b) => b.sessionsCount - a.sessionsCount);
  }, [views]);

  // Device
  const devices = useMemo(() => {
    const acc: Record<string, { device: string; sessions: Set<string>; views: number }> = {
      mobile: { device: "Mobile", sessions: new Set(), views: 0 },
      desktop: { device: "Desktop", sessions: new Set(), views: 0 },
      tablet: { device: "Tablet", sessions: new Set(), views: 0 },
      desconhecido: { device: "Desconhecido", sessions: new Set(), views: 0 },
    };
    for (const v of views) {
      const k = v.device || "desconhecido";
      const b = acc[k] ?? acc.desconhecido;
      b.views += 1;
      b.sessions.add(v.session_id);
    }
    return Object.values(acc)
      .map((r) => ({ device: r.device, sessions: r.sessions.size, views: r.views }))
      .filter((r) => r.views > 0);
  }, [views]);

  // Hora do dia
  const hourly = useMemo(() => {
    const arr = Array.from({ length: 24 }, (_, h) => ({ hour: `${h.toString().padStart(2, "0")}h`, Sessões: new Set<string>() }));
    for (const v of views) {
      const h = new Date(v.created_at).getHours();
      arr[h].Sessões.add(v.session_id);
    }
    return arr.map((r) => ({ hour: r.hour, Sessões: r.Sessões.size }));
  }, [views]);

  // Top CTAs
  const topCtas = useMemo(() => {
    const map = new Map<string, { label: string; count: number; paths: Set<string> }>();
    for (const c of clicks) {
      const key = c.button_label ?? c.button_id ?? "?";
      const r = map.get(key) ?? { label: key, count: 0, paths: new Set<string>() };
      r.count += 1;
      r.paths.add(c.path);
      map.set(key, r);
    }
    return Array.from(map.values()).sort((a, b) => b.count - a.count).slice(0, 15);
  }, [clicks]);

  return (
    <>
      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="p-5 bg-slate-900/60 border-slate-800 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-semibold text-white">Fontes de tráfego</h2>
              <p className="text-xs text-white/55">Source × medium — de onde as sessões vieram.</p>
            </div>
            <Button variant="outline" size="sm" onClick={() => downloadCsv("fontes-trafego", sources.map((s) => ({ source: s.source, medium: s.medium, sessoes: s.sessionsCount, views: s.views })))} className="h-8 bg-slate-950/50 border-slate-800 text-white/70">
              <Download className="h-3 w-3 mr-1" /> CSV
            </Button>
          </div>
          <div className="max-h-80 overflow-y-auto">
            <Table>
              <TableHeader className="sticky top-0 bg-slate-900">
                <TableRow className="border-slate-800 hover:bg-transparent">
                  <TableHead className="text-white/55">Source</TableHead>
                  <TableHead className="text-white/55">Medium</TableHead>
                  <TableHead className="text-right text-white/55">Sessões</TableHead>
                  <TableHead className="text-right text-white/55">Views</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sources.length === 0 ? (
                  <TableRow><TableCell colSpan={4} className="text-center text-white/45 py-8">Sem tráfego no período.</TableCell></TableRow>
                ) : sources.slice(0, 30).map((r) => (
                  <TableRow key={r.key} className="border-slate-800 hover:bg-slate-800/30">
                    <TableCell className="font-mono text-xs text-white/70">{r.source}</TableCell>
                    <TableCell className="font-mono text-xs text-white/55">{r.medium}</TableCell>
                    <TableCell className="text-right font-semibold text-white">{fmtNum(r.sessionsCount)}</TableCell>
                    <TableCell className="text-right text-white/55">{fmtNum(r.views)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>

        <Card className="p-5 bg-slate-900/60 border-slate-800">
          <h2 className="font-semibold text-white mb-1">Dispositivo</h2>
          <p className="text-xs text-white/55 mb-4">Distribuição das sessões.</p>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={devices} dataKey="sessions" nameKey="device" cx="50%" cy="50%" innerRadius={45} outerRadius={80} paddingAngle={3}>
                  {devices.map((_, i) => (
                    <Cell key={i} fill={["#3b82f6", "#10b981", "#f59e0b", "#64748b"][i % 4]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 8, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="p-5 bg-slate-900/60 border-slate-800">
          <h2 className="font-semibold text-white mb-1">Tráfego por hora do dia</h2>
          <p className="text-xs text-white/55 mb-4">Melhor janela pra disparos e mídia paga.</p>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={hourly} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis dataKey="hour" fontSize={9} stroke="#64748b" interval={1} />
                <YAxis fontSize={10} stroke="#64748b" allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="Sessões" fill="#f5b022" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-5 bg-slate-900/60 border-slate-800">
          <h2 className="font-semibold text-white mb-1">Top campanhas (UTM)</h2>
          <p className="text-xs text-white/55 mb-4">Ordenado por sessões.</p>
          <div className="max-h-64 overflow-y-auto">
            <Table>
              <TableHeader className="sticky top-0 bg-slate-900">
                <TableRow className="border-slate-800 hover:bg-transparent">
                  <TableHead className="text-white/55">Campanha</TableHead>
                  <TableHead className="text-white/55">Source/Med</TableHead>
                  <TableHead className="text-right text-white/55">Sessões</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {campaigns.length === 0 ? (
                  <TableRow><TableCell colSpan={3} className="text-center text-white/45 py-6">Nenhuma UTM no período.</TableCell></TableRow>
                ) : campaigns.slice(0, 20).map((r) => (
                  <TableRow key={r.campaign} className="border-slate-800 hover:bg-slate-800/30">
                    <TableCell className="font-mono text-xs text-white/70 truncate max-w-[180px]">{r.campaign}</TableCell>
                    <TableCell className="text-[10px] text-white/45">{r.source}/{r.medium}</TableCell>
                    <TableCell className="text-right font-semibold text-white">{fmtNum(r.sessionsCount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      </div>

      <Card className="p-5 bg-slate-900/60 border-slate-800">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-semibold text-white">Botões mais clicados</h2>
            <p className="text-xs text-white/55">CTAs que mais engajam.</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => downloadCsv("top-ctas", topCtas.map((c) => ({ botao: c.label, cliques: c.count, paginas: c.paths.size })))} className="h-8 bg-slate-950/50 border-slate-800 text-white/70">
            <Download className="h-3 w-3 mr-1" /> CSV
          </Button>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="border-slate-800 hover:bg-transparent">
              <TableHead className="text-white/55">Botão</TableHead>
              <TableHead className="text-right text-white/55">Cliques</TableHead>
              <TableHead className="text-right text-white/55">Páginas</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {topCtas.length === 0 ? (
              <TableRow><TableCell colSpan={3} className="text-center text-white/45 py-6">Sem cliques.</TableCell></TableRow>
            ) : topCtas.map((c) => (
              <TableRow key={c.label} className="border-slate-800 hover:bg-slate-800/30">
                <TableCell className="text-slate-200">{c.label}</TableCell>
                <TableCell className="text-right font-semibold text-white">{fmtNum(c.count)}</TableCell>
                <TableCell className="text-right text-white/55">{c.paths.size}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </>
  );
}

// ============================================================================
// TAB: CONVERSION
// ============================================================================

function ConversionTab({ views, clicks, leads, sales }: { views: ViewRow[]; clicks: ClickRow[]; leads: LeadRow[]; sales: SaleRow[] }) {
  // Per-page conversion table
  const pages = useMemo(() => {
    type Row = { path: string; sessions: Set<string>; views: number; clicks: number; leads: number; hot: number; sales: number; revenue: number };
    const map = new Map<string, Row>();
    const ensure = (p: string): Row => {
      const r = map.get(p) ?? { path: p, sessions: new Set<string>(), views: 0, clicks: 0, leads: 0, hot: 0, sales: 0, revenue: 0 };
      map.set(p, r);
      return r;
    };
    for (const v of views) {
      const r = ensure(v.path);
      r.views += 1;
      r.sessions.add(v.session_id);
    }
    for (const c of clicks) ensure(c.path).clicks += 1;
    const leadIndex = new Map(leads.map((l) => [l.id, l]));
    for (const l of leads) {
      const p = pathFromLead(l);
      if (!p) continue;
      const r = ensure(p);
      r.leads += 1;
      if (l.classificacao === "quente") r.hot += 1;
    }
    for (const s of sales) {
      let p: string | null = null;
      if (s.attributed_lead_id) {
        const l = leadIndex.get(s.attributed_lead_id);
        if (l) p = pathFromLead(l);
      }
      if (!p && s.utm_content && s.utm_content.startsWith("/")) p = s.utm_content;
      if (!p) continue;
      const r = ensure(p);
      r.sales += 1;
      r.revenue += Number(s.revenue_value ?? s.gross_value ?? 0);
    }
    return Array.from(map.values()).sort((a, b) => b.sessions.size - a.sessions.size);
  }, [views, clicks, leads, sales]);

  const [sortKey, setSortKey] = useState<"sessions" | "leads" | "sales" | "revenue" | "convLead" | "convSale">("sessions");
  const sortedPages = useMemo(() => {
    const arr = [...pages];
    arr.sort((a, b) => {
      const av = {
        sessions: a.sessions.size,
        leads: a.leads,
        sales: a.sales,
        revenue: a.revenue,
        convLead: a.sessions.size > 0 ? a.leads / a.sessions.size : 0,
        convSale: a.sessions.size > 0 ? a.sales / a.sessions.size : 0,
      }[sortKey];
      const bv = {
        sessions: b.sessions.size,
        leads: b.leads,
        sales: b.sales,
        revenue: b.revenue,
        convLead: b.sessions.size > 0 ? b.leads / b.sessions.size : 0,
        convSale: b.sessions.size > 0 ? b.sales / b.sessions.size : 0,
      }[sortKey];
      return bv - av;
    });
    return arr;
  }, [pages, sortKey]);

  // Classificação distribuição
  const classBreak = useMemo(() => {
    const c = { quente: 0, morno: 0, frio: 0 };
    for (const l of leads) {
      const k = (l.classificacao ?? "frio") as keyof typeof c;
      if (k in c) c[k] += 1;
    }
    return [
      { name: "Frio", value: c.frio, color: "#64748b" },
      { name: "Morno", value: c.morno, color: "#f59e0b" },
      { name: "Quente", value: c.quente, color: "#ef4444" },
    ];
  }, [leads]);

  // Origem (família de captura)
  const bySource = useMemo(() => {
    const map = new Map<string, { source: string; leads: number; hot: number }>();
    for (const l of leads) {
      const s = sourceFamily(l);
      const r = map.get(s) ?? { source: s, leads: 0, hot: 0 };
      r.leads += 1;
      if (l.classificacao === "quente") r.hot += 1;
      map.set(s, r);
    }
    return Array.from(map.values()).sort((a, b) => b.leads - a.leads);
  }, [leads]);

  return (
    <>
      <Card className="p-5 bg-slate-900/60 border-slate-800">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <h2 className="font-semibold text-white">Performance por página</h2>
            <p className="text-xs text-white/55">Do 1º acesso até a venda paga, por rota. Clique nas colunas pra ordenar.</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => downloadCsv("performance-pages", sortedPages.map((p) => ({ path: p.path, sessoes: p.sessions.size, views: p.views, cliques: p.clicks, leads: p.leads, quentes: p.hot, vendas: p.sales, faturamento: Math.round(p.revenue) })))} className="h-8 bg-slate-950/50 border-slate-800 text-white/70">
            <Download className="h-3 w-3 mr-1" /> CSV
          </Button>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-800 hover:bg-transparent">
                <TableHead className="text-white/55">Página</TableHead>
                <SortHead label="Sessões" k="sessions" curr={sortKey} set={setSortKey} />
                <SortHead label="Leads" k="leads" curr={sortKey} set={setSortKey} />
                <SortHead label="Conv. lead" k="convLead" curr={sortKey} set={setSortKey} />
                <SortHead label="Vendas" k="sales" curr={sortKey} set={setSortKey} />
                <SortHead label="Conv. venda" k="convSale" curr={sortKey} set={setSortKey} />
                <SortHead label="Faturamento" k="revenue" curr={sortKey} set={setSortKey} />
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedPages.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center text-white/45 py-8">Sem dados no período.</TableCell></TableRow>
              ) : sortedPages.slice(0, 50).map((p) => (
                <TableRow key={p.path} className="border-slate-800 hover:bg-slate-800/30">
                  <TableCell className="font-mono text-xs text-white/70">{p.path}</TableCell>
                  <TableCell className="text-right font-semibold text-white">{fmtNum(p.sessions.size)}</TableCell>
                  <TableCell className="text-right text-admin-accent">{p.leads > 0 ? fmtNum(p.leads) : "—"}{p.hot > 0 && <span className="text-[9px] text-rose-400 ml-1">🔥{p.hot}</span>}</TableCell>
                  <TableCell className="text-right text-xs text-white/55">{pct(p.leads, p.sessions.size)}</TableCell>
                  <TableCell className="text-right text-emerald-400 font-semibold">{p.sales > 0 ? fmtNum(p.sales) : "—"}</TableCell>
                  <TableCell className="text-right text-xs text-white/55">{pct(p.sales, p.sessions.size)}</TableCell>
                  <TableCell className="text-right text-xs text-white">{p.revenue > 0 ? fmtBRL(p.revenue) : "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="p-5 bg-slate-900/60 border-slate-800">
          <h2 className="font-semibold text-white mb-1">Qualificação de leads</h2>
          <p className="text-xs text-white/55 mb-4">Distribuição Frio → Quente.</p>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={classBreak} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={(e: { name?: string; percent?: number }) => `${e.name ?? ""} ${(((e.percent ?? 0)) * 100).toFixed(0)}%`}>
                  {classBreak.map((e, i) => <Cell key={i} fill={e.color} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 8, fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-5 bg-slate-900/60 border-slate-800">
          <h2 className="font-semibold text-white mb-1">Leads por fonte de captura</h2>
          <p className="text-xs text-white/55 mb-4">Aulas, ementas, pré-checkout, revista, quizz…</p>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={bySource} layout="vertical" margin={{ top: 5, right: 20, left: 60, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis type="number" fontSize={10} stroke="#64748b" allowDecimals={false} />
                <YAxis dataKey="source" type="category" fontSize={10} stroke="#64748b" width={80} />
                <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 8, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="leads" name="Leads" fill="#f59e0b" radius={[0, 4, 4, 0]} />
                <Bar dataKey="hot" name="Quentes" fill="#ef4444" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>
    </>
  );
}

function SortHead<K extends string>({ label, k, curr, set }: { label: string; k: K; curr: K; set: (k: K) => void }) {
  const active = k === curr;
  return (
    <TableHead
      className={`text-right cursor-pointer select-none ${active ? "text-admin-accent" : "text-white/55"} hover:text-white`}
      onClick={() => set(k)}
    >
      {label} {active && "↓"}
    </TableHead>
  );
}

// ============================================================================
// TAB: PRODUCTS
// ============================================================================

function ProductsTab({ sales, leads }: { sales: SaleRow[]; leads: LeadRow[] }) {
  const byProduct = useMemo(() => {
    const map = new Map<string, { group: string; label: string; count: number; revenue: number; color: string }>();
    for (const s of sales) {
      const g = productGroup(s.product_name);
      const r = map.get(g) ?? { group: g, label: PRODUCT_LABEL[g] ?? g, count: 0, revenue: 0, color: PRODUCT_COLOR[g] ?? "#64748b" };
      r.count += 1;
      r.revenue += Number(s.revenue_value ?? s.gross_value ?? 0);
      map.set(g, r);
    }
    return Array.from(map.values()).sort((a, b) => b.revenue - a.revenue);
  }, [sales]);

  // Produtos específicos (SKU)
  const bySku = useMemo(() => {
    const map = new Map<string, { name: string; count: number; revenue: number }>();
    for (const s of sales) {
      const name = s.product_name ?? "(sem nome)";
      const r = map.get(name) ?? { name, count: 0, revenue: 0 };
      r.count += 1;
      r.revenue += Number(s.revenue_value ?? s.gross_value ?? 0);
      map.set(name, r);
    }
    return Array.from(map.values()).sort((a, b) => b.revenue - a.revenue);
  }, [sales]);

  const totalRevenue = byProduct.reduce((s, r) => s + r.revenue, 0);
  void leads;

  return (
    <>
      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="p-5 bg-slate-900/60 border-slate-800 lg:col-span-1">
          <h2 className="font-semibold text-white mb-1">Faturamento por produto</h2>
          <p className="text-xs text-white/55 mb-4">Share da receita paga.</p>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={byProduct} dataKey="revenue" nameKey="label" cx="50%" cy="50%" innerRadius={50} outerRadius={90} paddingAngle={2}>
                  {byProduct.map((e, i) => <Cell key={i} fill={e.color} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 8, fontSize: 12 }} formatter={(v: number) => fmtBRL(v)} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-5 bg-slate-900/60 border-slate-800 lg:col-span-2">
          <h2 className="font-semibold text-white mb-1">Ranking de produtos</h2>
          <p className="text-xs text-white/55 mb-4">Grupo de produto — vendas e ticket médio.</p>
          <Table>
            <TableHeader>
              <TableRow className="border-slate-800 hover:bg-transparent">
                <TableHead className="text-white/55">Produto</TableHead>
                <TableHead className="text-right text-white/55">Vendas</TableHead>
                <TableHead className="text-right text-white/55">Ticket médio</TableHead>
                <TableHead className="text-right text-white/55">Faturamento</TableHead>
                <TableHead className="text-right text-white/55">Share</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {byProduct.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center text-white/45 py-8">Sem vendas no período.</TableCell></TableRow>
              ) : byProduct.map((p) => (
                <TableRow key={p.group} className="border-slate-800 hover:bg-slate-800/30">
                  <TableCell><span className="inline-flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ background: p.color }} /><span className="text-slate-200 font-medium">{p.label}</span></span></TableCell>
                  <TableCell className="text-right text-white font-semibold">{fmtNum(p.count)}</TableCell>
                  <TableCell className="text-right text-white/55 text-xs">{fmtBRL(p.count > 0 ? p.revenue / p.count : 0)}</TableCell>
                  <TableCell className="text-right text-emerald-400 font-semibold">{fmtBRL(p.revenue)}</TableCell>
                  <TableCell className="text-right text-xs text-white/55">{pct(p.revenue, totalRevenue)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </div>

      <Card className="p-5 bg-slate-900/60 border-slate-800">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-semibold text-white">Vendas por SKU (Eduzz)</h2>
            <p className="text-xs text-white/55">Nome exato do produto — útil pra reconciliar campanhas específicas.</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => downloadCsv("vendas-sku", bySku.map((r) => ({ produto: r.name, vendas: r.count, faturamento: Math.round(r.revenue) })))} className="h-8 bg-slate-950/50 border-slate-800 text-white/70">
            <Download className="h-3 w-3 mr-1" /> CSV
          </Button>
        </div>
        <div className="max-h-96 overflow-y-auto">
          <Table>
            <TableHeader className="sticky top-0 bg-slate-900">
              <TableRow className="border-slate-800 hover:bg-transparent">
                <TableHead className="text-white/55">Produto</TableHead>
                <TableHead className="text-right text-white/55">Vendas</TableHead>
                <TableHead className="text-right text-white/55">Faturamento</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {bySku.length === 0 ? (
                <TableRow><TableCell colSpan={3} className="text-center text-white/45 py-6">Sem vendas.</TableCell></TableRow>
              ) : bySku.slice(0, 50).map((r) => (
                <TableRow key={r.name} className="border-slate-800 hover:bg-slate-800/30">
                  <TableCell className="text-xs text-white/70 truncate max-w-md">{r.name}</TableCell>
                  <TableCell className="text-right text-white">{r.count}</TableCell>
                  <TableCell className="text-right text-emerald-400">{fmtBRL(r.revenue)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </>
  );
}

// ============================================================================
// TAB: ATTRIBUTION
// ============================================================================

function AttributionTab({ views, leads, sales }: { views: ViewRow[]; leads: LeadRow[]; sales: SaleRow[] }) {
  void views;
  // Atribuição por lead → source (via lead.origem/landing_path)
  const leadIndex = useMemo(() => new Map(leads.map((l) => [l.id, l])), [leads]);

  const attribution = useMemo(() => {
    type Row = { key: string; source: string; medium: string; campaign: string; leads: number; sales: number; revenue: number };
    const map = new Map<string, Row>();
    // Contabiliza vendas por UTM da venda
    for (const s of sales) {
      const source = s.utm_source ?? "(direto)";
      const medium = s.utm_medium ?? "(nenhum)";
      const campaign = s.utm_campaign ?? "—";
      const k = `${source}|${medium}|${campaign}`;
      const r = map.get(k) ?? { key: k, source, medium, campaign, leads: 0, sales: 0, revenue: 0 };
      r.sales += 1;
      r.revenue += Number(s.revenue_value ?? s.gross_value ?? 0);
      map.set(k, r);
      // Se tem lead atribuído, marca 1 lead nesse combo
      if (s.attributed_lead_id) {
        const l = leadIndex.get(s.attributed_lead_id);
        if (l) r.leads += 1;
      }
    }
    return Array.from(map.values()).sort((a, b) => b.revenue - a.revenue);
  }, [sales, leadIndex]);

  const totalRev = attribution.reduce((s, r) => s + r.revenue, 0);

  // Direct vs paid vs organic (roll-up)
  const rollup = useMemo(() => {
    const groups: Record<string, { name: string; sales: number; revenue: number; color: string }> = {
      pago: { name: "Pago (Ads)", sales: 0, revenue: 0, color: "#f59e0b" },
      organico: { name: "Orgânico", sales: 0, revenue: 0, color: "#10b981" },
      email: { name: "E-mail", sales: 0, revenue: 0, color: "#3b82f6" },
      crm: { name: "CRM/WhatsApp", sales: 0, revenue: 0, color: "#a855f7" },
      direto: { name: "Direto/Outros", sales: 0, revenue: 0, color: "#64748b" },
    };
    for (const s of sales) {
      const src = (s.utm_source ?? "").toLowerCase();
      const med = (s.utm_medium ?? "").toLowerCase();
      let key = "direto";
      if (src === "crm") key = "crm";
      else if (med.includes("cpc") || med.includes("paid") || src.includes("meta") || src.includes("google") || src.includes("fb") || src.includes("ig")) key = "pago";
      else if (med.includes("email") || src.includes("rd") || src.includes("mail")) key = "email";
      else if (med.includes("organic") || src.includes("google-organic") || src.includes("seo")) key = "organico";
      groups[key].sales += 1;
      groups[key].revenue += Number(s.revenue_value ?? s.gross_value ?? 0);
    }
    return Object.values(groups).filter((g) => g.sales > 0);
  }, [sales]);

  return (
    <>
      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="p-5 bg-slate-900/60 border-slate-800">
          <h2 className="font-semibold text-white mb-1">Receita por canal</h2>
          <p className="text-xs text-white/55 mb-4">Roll-up dos UTMs de venda em canais.</p>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={rollup} dataKey="revenue" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={(e: { name?: string; percent?: number }) => `${e.name ?? ""} ${(((e.percent ?? 0)) * 100).toFixed(0)}%`}>
                  {rollup.map((e, i) => <Cell key={i} fill={e.color} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 8, fontSize: 12 }} formatter={(v: number) => fmtBRL(v)} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-5 bg-slate-900/60 border-slate-800">
          <h2 className="font-semibold text-white mb-1">Vendas por canal</h2>
          <p className="text-xs text-white/55 mb-4">Contagem e ticket médio.</p>
          <Table>
            <TableHeader>
              <TableRow className="border-slate-800 hover:bg-transparent">
                <TableHead className="text-white/55">Canal</TableHead>
                <TableHead className="text-right text-white/55">Vendas</TableHead>
                <TableHead className="text-right text-white/55">Ticket</TableHead>
                <TableHead className="text-right text-white/55">Faturam.</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rollup.length === 0 ? (
                <TableRow><TableCell colSpan={4} className="text-center text-white/45 py-6">Sem vendas.</TableCell></TableRow>
              ) : rollup.map((r) => (
                <TableRow key={r.name} className="border-slate-800 hover:bg-slate-800/30">
                  <TableCell><span className="inline-flex items-center gap-2"><span className="h-2 w-2 rounded-full" style={{ background: r.color }} /><span className="text-slate-200">{r.name}</span></span></TableCell>
                  <TableCell className="text-right text-white font-semibold">{r.sales}</TableCell>
                  <TableCell className="text-right text-white/55 text-xs">{fmtBRL(r.sales > 0 ? r.revenue / r.sales : 0)}</TableCell>
                  <TableCell className="text-right text-emerald-400">{fmtBRL(r.revenue)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </div>

      <Card className="p-5 bg-slate-900/60 border-slate-800">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-semibold text-white">Atribuição detalhada</h2>
            <p className="text-xs text-white/55">Source × medium × campanha — do clique à receita.</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => downloadCsv("atribuicao", attribution.map((r) => ({ source: r.source, medium: r.medium, campanha: r.campaign, leads: r.leads, vendas: r.sales, faturamento: Math.round(r.revenue) })))} className="h-8 bg-slate-950/50 border-slate-800 text-white/70">
            <Download className="h-3 w-3 mr-1" /> CSV
          </Button>
        </div>
        <div className="max-h-96 overflow-y-auto">
          <Table>
            <TableHeader className="sticky top-0 bg-slate-900">
              <TableRow className="border-slate-800 hover:bg-transparent">
                <TableHead className="text-white/55">Source</TableHead>
                <TableHead className="text-white/55">Medium</TableHead>
                <TableHead className="text-white/55">Campanha</TableHead>
                <TableHead className="text-right text-white/55">Leads</TableHead>
                <TableHead className="text-right text-white/55">Vendas</TableHead>
                <TableHead className="text-right text-white/55">Faturam.</TableHead>
                <TableHead className="text-right text-white/55">Share</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {attribution.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center text-white/45 py-8">Sem vendas atribuíveis.</TableCell></TableRow>
              ) : attribution.slice(0, 50).map((r) => (
                <TableRow key={r.key} className="border-slate-800 hover:bg-slate-800/30">
                  <TableCell className="font-mono text-xs text-white/70">{r.source}</TableCell>
                  <TableCell className="font-mono text-xs text-white/55">{r.medium}</TableCell>
                  <TableCell className="font-mono text-xs text-white/55 truncate max-w-[180px]">{r.campaign}</TableCell>
                  <TableCell className="text-right text-admin-accent">{r.leads > 0 ? r.leads : "—"}</TableCell>
                  <TableCell className="text-right text-white font-semibold">{r.sales}</TableCell>
                  <TableCell className="text-right text-emerald-400">{fmtBRL(r.revenue)}</TableCell>
                  <TableCell className="text-right text-xs text-white/55">{pct(r.revenue, totalRev)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </>
  );
}

// ============================================================================
// TAB: INSIGHTS (auto-observações + atalhos)
// ============================================================================

function InsightsTab({ data, prev, hasComparison }: { data: Overview; prev: Overview | null; hasComparison: boolean }) {
  const insights = useMemo(() => {
    const out: { type: "up" | "down" | "info" | "warn"; title: string; desc: string }[] = [];
    const k = computeKpis(data);
    const pk = prev ? computeKpis(prev) : null;

    if (hasComparison && pk) {
      const rev = delta(k.revenue, pk.revenue);
      if (Math.abs(rev) >= 15) {
        out.push({
          type: rev > 0 ? "up" : "down",
          title: `Faturamento ${rev > 0 ? "cresceu" : "caiu"} ${Math.abs(rev).toFixed(0)}%`,
          desc: `De ${fmtBRL(pk.revenue)} para ${fmtBRL(k.revenue)} vs. período anterior.`,
        });
      }
      const conv = delta(k.leads / Math.max(k.sessions, 1), pk.leads / Math.max(pk.sessions, 1));
      if (Math.abs(conv) >= 20) {
        out.push({
          type: conv > 0 ? "up" : "down",
          title: `Conversão de lead ${conv > 0 ? "subiu" : "caiu"} ${Math.abs(conv).toFixed(0)}%`,
          desc: `Sessão → lead: ${pct(k.leads, k.sessions)} (antes ${pct(pk.leads, pk.sessions)}).`,
        });
      }
      const sess = delta(k.sessions, pk.sessions);
      if (Math.abs(sess) >= 25) {
        out.push({
          type: sess > 0 ? "up" : "down",
          title: `Tráfego ${sess > 0 ? "aumentou" : "diminuiu"} ${Math.abs(sess).toFixed(0)}%`,
          desc: `${fmtNum(k.sessions)} sessões únicas vs. ${fmtNum(pk.sessions)} antes.`,
        });
      }
    }

    // Página com maior potencial (muita sessão, pouca conversão)
    const pathAgg = new Map<string, { sessions: Set<string>; leads: number }>();
    for (const v of data.views) {
      const r = pathAgg.get(v.path) ?? { sessions: new Set<string>(), leads: 0 };
      r.sessions.add(v.session_id);
      pathAgg.set(v.path, r);
    }
    for (const l of data.leads) {
      const p = pathFromLead(l);
      if (!p) continue;
      const r = pathAgg.get(p);
      if (r) r.leads += 1;
    }
    const alerts = Array.from(pathAgg.entries())
      .filter(([, r]) => r.sessions.size >= 30)
      .map(([path, r]) => ({ path, sess: r.sessions.size, conv: r.leads / r.sessions.size }))
      .sort((a, b) => a.conv - b.conv);
    const worst = alerts[0];
    if (worst && worst.conv < 0.02) {
      out.push({
        type: "warn",
        title: `Atenção: ${worst.path} está convertendo pouco`,
        desc: `${fmtNum(worst.sess)} sessões, apenas ${(worst.conv * 100).toFixed(1)}% viraram lead.`,
      });
    }
    const best = alerts[alerts.length - 1];
    if (best && best.conv > 0.15) {
      out.push({
        type: "up",
        title: `${best.path} é sua página que mais converte`,
        desc: `${(best.conv * 100).toFixed(1)}% dos visitantes viraram lead — vale escalar tráfego.`,
      });
    }

    // CRM ativo?
    const crmSales = data.sales.filter((s) => (s.utm_source ?? "").toLowerCase() === "crm").length;
    if (crmSales > 0) {
      out.push({
        type: "info",
        title: `Time comercial fechou ${crmSales} venda(s) via CRM/WhatsApp`,
        desc: "Vendas com utm_source=crm — atribuídas ao time comercial.",
      });
    }

    if (out.length === 0) {
      out.push({ type: "info", title: "Sem alertas relevantes", desc: "Métricas estáveis vs. período anterior." });
    }
    return out;
  }, [data, prev, hasComparison]);

  return (
    <>
      <div className="grid gap-4 md:grid-cols-2">
        {insights.map((ins, i) => {
          const styles = {
            up: { bg: "bg-emerald-500/10", border: "border-emerald-500/30", ic: "text-emerald-400", icon: <ArrowUpRight className="h-5 w-5" /> },
            down: { bg: "bg-rose-500/10", border: "border-rose-500/30", ic: "text-rose-400", icon: <ArrowDownRight className="h-5 w-5" /> },
            warn: { bg: "bg-amber-500/10", border: "border-amber-500/30", ic: "text-admin-accent", icon: <Sparkles className="h-5 w-5" /> },
            info: { bg: "bg-blue-500/10", border: "border-blue-500/30", ic: "text-blue-400", icon: <Sparkles className="h-5 w-5" /> },
          }[ins.type];
          return (
            <Card key={i} className={`p-4 ${styles.bg} ${styles.border} border`}>
              <div className="flex items-start gap-3">
                <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${styles.ic} bg-slate-950/40`}>
                  {styles.icon}
                </div>
                <div>
                  <p className={`font-semibold text-sm ${styles.ic}`}>{ins.title}</p>
                  <p className="text-xs text-white/70 mt-1">{ins.desc}</p>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      <Card className="p-5 bg-slate-900/60 border-slate-800">
        <h2 className="font-semibold text-white mb-3">Atalhos operacionais</h2>
        <p className="text-xs text-white/55 mb-4">Fluxos rápidos pra time de marketing.</p>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <ShortcutCard to="/admin/leads" title="Ver leads" desc="Filtro, histórico, retry de entrega" />
          <ShortcutCard to="/admin/placar" title="Placar do Jogo" desc="Gasto, leads e vendas do mês" />
          <ShortcutCard to="/admin/campanhas" title="Gerir campanhas" desc="Cupons ativos & plantão" />
          <ShortcutCard to="/admin/reports" title="Reports agendados" desc="E-mail para stakeholders" />
          <ShortcutCard to="/admin/painel" title="Painel técnico" desc="Health-check de LPs" />
          <ShortcutCard to="/admin/depoimentos" title="Depoimentos" desc="Curadoria social proof" />
        </div>
      </Card>
    </>
  );
}

function ShortcutCard({ to, title, desc }: { to: string; title: string; desc: string }) {
  return (
    <Link to={to} className="group rounded-lg border border-slate-800 bg-slate-950/40 p-4 hover:border-[#f5b022]/40 hover:bg-slate-800/40 transition-all">
      <p className="text-sm font-semibold text-white group-hover:text-admin-accent">{title}</p>
      <p className="text-xs text-white/55 mt-1">{desc}</p>
    </Link>
  );
}
